import React from 'react'
import ConfiguratorAppContext from '../ConfiguratorAppContext'
import { ProductDataContext } from '../ProductDataContext'
import { useProductWithId, useRoomWithId } from '../hooks/useProductWithId'
import lodash from 'lodash'
import { InflatedListItemProduct, ListRoom } from './ListTypes'
import { AppStateActions } from '../state/AppState'
import { SectionHeaderRow } from './SectionHeaderRow'
import HintWrapper from '../components/HintWrapper'
import { HintPosition } from '../Enums'
import Button from '../components/Button'
import { IconMenuDots } from '../components/icons'
import { RoomHeader, RoomSection } from './RoomSection'
import ExpandToggle from '../components/ExpandToggle'
import { BomItem, generateFullBom, getBomProductsForInflatedItem, PricingParams } from '../bom/bomUtils'
import {
  getListItemPricing,
  isBulkPackProduct,
  ListItemPricing,
  TotalPriceForBomResult,
} from '../productPricing/productPricingCalculation'
import { UserType } from '../types/userTypes'
import ListItemRow from './ListItemRow'
import { ProductPriceNoData, ProductPriceUnavailable } from '../productPricing/ProductPricingContext'
import AddRoomRow from './AddRoomRow'
import { getListInterfaceDisplayName } from './listItemUtils'
import { ComponentListItemRow } from '../components/ComponentListItemRow'
import { ListPrice } from './ListPrice'
import RenameDeletePopup from './RenameDeleteMenuPopup'
import RenameRoomPopup from './RenameRoomPopup'
import DeleteRoomPopup from './DeleteRoomPopup'
import ClearListPopup from './ClearListPopup'
import DeleteProductPopup from './DeleteProductPopup'
import { getAnalyticsProductInfo, getEcommercePayloadFrom } from '../analytics/AnalyticsHelpers'
import { v4 as uuidv4 } from 'uuid'
import { BuildConfig } from '../config/BuildConfig'
import { AnalyticsContext } from '../analytics/AnalyticsContext'

export interface ListManagerProps {
  configuredProducts: readonly InflatedListItemProduct[]
  rooms: readonly ListRoom[]
  fullListBom: readonly BomItem[]
  focusedItemUid: string | null

  pricingParams: PricingParams
  shouldShowPricing: boolean
  getPackQuantity: (itemNo: string) => (number | null)
  isFetchingPricing: boolean
  totalPrice: TotalPriceForBomResult | null
}

/**
 * Shows the user's list (rooms and products) and pricing (if enabled), and allows them to manage it.
 */
export function ListManager({
  configuredProducts,
  rooms,
  fullListBom,
  focusedItemUid,
  pricingParams,
  shouldShowPricing,
  getPackQuantity,
  isFetchingPricing,
  totalPrice,
}: ListManagerProps) {
  const { listName, listId, dispatch, renameListTutorialDismissed, userType, addingRoomsEnabled } = React.useContext(ConfiguratorAppContext)
  const Products = React.useContext(ProductDataContext)
  const analyticsLogger = React.useContext(AnalyticsContext)

  const [hasUserTypeInitially] = React.useState(!!userType)
  const shouldShowRenameHint = hasUserTypeInitially && !renameListTutorialDismissed

  const [expandedItemUid, setExpandedItemUid] = React.useState<string | null>(null)

  React.useLayoutEffect(() => {
    if (focusedItemUid !== null) {
      setExpandedItemUid(state => state !== focusedItemUid ? null : state)
    }
  }, [focusedItemUid, setExpandedItemUid])

  const [newRoomName, setNewRoomName] = React.useState<string | null>(null)

  const [menuOpenList, setMenuOpenList] = React.useState(false)
  const [renamingList, setRenameList] = React.useState(false)
  const [deletingList, setDeletingList] = React.useState(false)

  const [deletingProduct, setDeletingProductId] = useProductWithId(configuredProducts)

  const [menuOpenRoom, setMenuOpenRoom] = useRoomWithId(rooms)
  const [renamingRoom, setRenameRoomId] = useRoomWithId(rooms)
  const [deletingRoom, setDeletingRoomId] = useRoomWithId(rooms)

  const productsByRoom = lodash.groupBy(configuredProducts, it => it.roomId ?? '')

  const roomsToShow: readonly ListRoom[] = [{
    name: '',
    uid: '',
    expanded: true,
  }].concat(rooms)

  const onChangeQuantity = (product: InflatedListItemProduct, newQuantity: number) => {
    if (newQuantity === 0) {
      setDeletingProductId(product.uid)
    } else {
      dispatch({
        action: AppStateActions.ChangeQuantity,
        uid: product.uid,
        newQuantity,
      })
    }
  }

  const onChangeNote = (uid: string, newNote: string) => {
    dispatch({
      action: AppStateActions.ChangeNote,
      listItemUid: uid,
      newNote,
    })
  }

  const onChangeRoom = (uid: string, newRoomId: string | null) => {
    dispatch({
      action: AppStateActions.ChangeRoom,
      listItemUid: uid,
      roomId: newRoomId,
    })
  }

  const onClickDeleteRoom = (uid: string) => {
    if ((productsByRoom[uid] ?? []).length === 0) {
      onDeleteRoom(uid, false)
    } else {
      setDeletingRoomId(uid)
    }
  }

  const onDeleteRoom = (uid: string, keepItems: boolean) => {
    dispatch({ action: AppStateActions.DeleteRoom, uid: uid, keepItems })
  }

  return (
    <>
      <SectionHeaderRow title={listName}>
        <HintWrapper
          isShown={shouldShowRenameHint}
          appearDelayMs={1000}
          hintPosition={HintPosition.BelowLeft}
          message={'Click here to rename your list'}
          handleDismiss={() => {
            dispatch({ action: AppStateActions.RenameListTutorialDismissed })
          }}
        >
          <Button
            buttonStyle="grey"
            onClick={() => setMenuOpenList(true)}
            round
          >
            <img src={IconMenuDots} alt="menu"/>
          </Button>
        </HintWrapper>
      </SectionHeaderRow>

      {/* Rooms */}
      <React.Fragment key="rooms">
        {
          roomsToShow.map(room => {
            const productsInRoom = productsByRoom[room.uid] ?? []
            const isUnroomedSection = room.uid === ''
            if (isUnroomedSection && productsInRoom.length === 0) {
              return null
            }

            return (
              <RoomSection key={room.uid}>
                {
                  !isUnroomedSection && (
                    <RoomHeader
                      onClickMenu={() => setMenuOpenRoom(room.uid)}
                      rightContent={(
                        <>
                          <ExpandToggle
                            onClick={() => dispatch({
                              action: AppStateActions.RoomExpanded,
                              roomUid: room.uid,
                              expanded: !room.expanded,
                            })}
                            expanded={room.expanded}
                          />
                          <div style={{ flex: '0 0 10px' }}/>
                        </>
                      )}
                    >
                      {room.name}
                    </RoomHeader>
                  )
                }

                {room.expanded && (
                  <>
                    {productsInRoom.length === 0 && (
                      <div className="emptyRoom">
                        This room is empty.
                      </div>
                    )}

                    {
                      productsInRoom.map((configuredProduct, i) => {
                        const listItemBom = generateFullBom([getBomProductsForInflatedItem(configuredProduct)], getPackQuantity)

                        const productPriceParams: ListItemPricing | null = shouldShowPricing ? getListItemPricing(listItemBom, pricingParams.priceMap) : null

                        const isHomeOwnerViewingProProduct: boolean = !!configuredProduct.skinProduct.pro && userType === UserType.Consumer

                        return (
                          <ListItemRow
                            isConfigureDisabled={isHomeOwnerViewingProProduct}
                            isFocused={!shouldShowRenameHint && focusedItemUid === configuredProduct.uid}
                            key={configuredProduct.uid}
                            configuredProduct={configuredProduct}
                            onClickEdit={() => dispatch({
                              action: AppStateActions.EditProduct,
                              uid: configuredProduct.uid,
                            })}
                            onAddQuantity={quantityDelta => {
                              if (quantityDelta > 0) {
                                const addedItemBom = generateFullBom([getBomProductsForInflatedItem({ ...configuredProduct, quantity: quantityDelta })], getPackQuantity)
                                const addedItemPrice: ListItemPricing | null = shouldShowPricing ? getListItemPricing(addedItemBom, pricingParams.priceMap) : null

                                analyticsLogger.log({
                                  event: 'add_to_cart',
                                  distributor: null,
                                  add_to_cart_location: 'My list',
                                  cta_name: 'increase',
                                  ecommerce: getEcommercePayloadFrom(
                                    [{
                                      product: getAnalyticsProductInfo(configuredProduct.skinProduct),
                                      quantity: quantityDelta,
                                      price: addedItemPrice?.price?.toString() ?? null,
                                    }],
                                    {
                                      name: listName,
                                      id: listId
                                    }
                                  )
                                })
                              } else if (quantityDelta < 0) {
                                const quantityRemoved = Math.abs(quantityDelta)
                                const removedItemBom = generateFullBom([getBomProductsForInflatedItem({ ...configuredProduct, quantity: quantityRemoved })], getPackQuantity)
                                const removedItemPrice: ListItemPricing | null = shouldShowPricing ? getListItemPricing(removedItemBom, pricingParams.priceMap) : null

                                analyticsLogger.log({
                                  event: 'remove_from_cart',
                                  ecommerce: getEcommercePayloadFrom(
                                    [{
                                      product: getAnalyticsProductInfo(configuredProduct.skinProduct),
                                      quantity: quantityRemoved,
                                      price: removedItemPrice?.price?.toString() ?? null,
                                    }],
                                    {
                                      name: listName,
                                      id: listId
                                    }
                                  )
                                })
                              }

                              onChangeQuantity(configuredProduct, configuredProduct.quantity + quantityDelta)
                            }}
                            onDelete={() => {
                              onChangeQuantity(configuredProduct, 0)
                            }}
                            onChangeNote={it => onChangeNote(configuredProduct.uid, it)}
                            onChangeRoom={it => onChangeRoom(configuredProduct.uid, it)}
                            onClickDuplicate={() => dispatch({
                              action: AppStateActions.DuplicateProduct,
                              uid: configuredProduct.uid,
                            })}
                            onClickMore={() => setExpandedItemUid(state => state === configuredProduct.uid ? null : configuredProduct.uid)}
                            expanded={expandedItemUid === configuredProduct.uid}
                            rooms={rooms}
                            showPrice={shouldShowPricing}
                            priceLoading={productPriceParams?.isMissingData && isFetchingPricing}
                            price={productPriceParams?.price ?? ProductPriceUnavailable}
                            priceDisclaimer={productPriceParams?.disclaimer ?? null}
                              // isAuthenticated={isAuthenticated || isWholesalerMode()}
                            priceTypeLabel={pricingParams.label}
                          />
                        )
                      })
                    }
                  </>
                )}
              </RoomSection>
            )
          })
        }

        {addingRoomsEnabled && (
          <AddRoomRow
            onAddRoom={name => {
              dispatch({ action: AppStateActions.AddRoom, name })
              setNewRoomName(null)
            }}
            onChangeRoomName={setNewRoomName}
            editingRoomName={newRoomName ?? ''}
            editing={newRoomName !== null}
            onChangeEditMode={editing => {
              if (editing) {
                setNewRoomName(current => current ?? '')
              } else {
                setNewRoomName(null)
              }
            }}
          />
        )}

      </React.Fragment>

      {/* Bulk packs */}
      <>
        {
          (shouldShowPricing && !isFetchingPricing) && fullListBom
            .filter(isBulkPackProduct)
            .map(bomItem => {
              const priceEach = pricingParams.priceMap[bomItem.itemNo]
              // multiply by quantity (number of packs)
              const price = priceEach?.times(bomItem.quantity)

              // show the display name for interfaces, or fall back to description for other products
              const interfaceItem = Products.interfaceMap[bomItem.itemNo] ?? null
              const displayName = interfaceItem ? getListInterfaceDisplayName(interfaceItem, null) : bomItem.description

              return (
                <ComponentListItemRow
                  key={bomItem.itemNo}
                  bomItem={bomItem}
                  displayName={displayName}
                  priceLoading={price === ProductPriceNoData && isFetchingPricing}
                  price={price ?? ProductPriceUnavailable}
                  priceTypeLabel={pricingParams.label}
                  showPrice={shouldShowPricing}
                />
              )
            })
        }
      </>

      {/* Total price */}
      <>
        {
          totalPrice && (
            <div className="totalPrice">
              <ListPrice
                price={totalPrice.price}
                priceLoading={totalPrice?.isMissingData && isFetchingPricing}
                showPrice={shouldShowPricing}
                total
                priceTypeLabel={pricingParams.label}
              />
            </div>
          )
        }
      </>

      {/* popups */}

      {
        menuOpenRoom && (
          <RenameDeletePopup
            onClose={() => setMenuOpenRoom(null)}
            onRename={() => {
              analyticsLogger.log({
                event: 'cta_click',
                cta_name: 'Rename',
                cta_link: null,
                cta_category: 'Button',
                cta_location: 'My list'
              })

              setRenameRoomId(menuOpenRoom.uid)
              setMenuOpenRoom(null)
            }}
            onDelete={() => {
              analyticsLogger.log({
                event: 'cta_click',
                cta_name: 'Clear List',
                cta_link: null,
                cta_category: 'Button',
                cta_location: 'My list'
              })

              onClickDeleteRoom(menuOpenRoom.uid)
              setMenuOpenRoom(null)
            }}
            title={menuOpenRoom.name}
          />
        )
      }

      {
        menuOpenList && (
          <RenameDeletePopup
            deleteText="Clear List"
            onClose={() => setMenuOpenList(false)}
            onRename={() => {
              setRenameList(true)
              setMenuOpenList(false)
            }}
            onDelete={() => {
              setDeletingList(true)
              setMenuOpenList(false)
            }}
            title={listName}
          />
        )
      }

      {
        renamingRoom && (
          <RenameRoomPopup
            name={renamingRoom.name}
            onClose={() => setRenameRoomId(null)}
            onSave={newName => {
              dispatch({ action: AppStateActions.RenameRoom, uid: renamingRoom.uid, name: newName })
              setRenameRoomId(null)
            }}
          />
        )
      }

      {
        renamingList && (
          <RenameRoomPopup
            name={listName}
            onClose={() => setRenameList(false)}
            onSave={newName => {
              dispatch({ action: AppStateActions.RenameList, newName })
              setRenameList(false)
            }}
          />
        )
      }

      {
        deletingRoom && (
          <DeleteRoomPopup
            room={deletingRoom}
            onClose={() => setDeletingRoomId(null)}
            onConfirm={keepItems => onDeleteRoom(deletingRoom.uid, keepItems)}
            numProducts={productsByRoom[deletingRoom.uid]?.length ?? 0}
          />
        )
      }

      {
        deletingList && (
          <ClearListPopup
            listName={listName}
            onClose={() => setDeletingList(false)}
            onConfirm={() => {
              dispatch({ action: AppStateActions.ClearList, newListName: BuildConfig.brand.copy.DefaultListName, newListId: uuidv4() })
              setDeletingList(false)
            }}
          />
        )
      }

      {
        deletingProduct && (
          <DeleteProductPopup
            onClose={() => setDeletingProductId(null)}
            onConfirm={() => {
              const listItemBom = generateFullBom([getBomProductsForInflatedItem(deletingProduct)], getPackQuantity)
              const price = getListItemPricing(listItemBom, pricingParams.priceMap).price?.toString() ?? null

              analyticsLogger.log({
                event: 'remove_from_cart',
                ecommerce: getEcommercePayloadFrom(
                  [{
                    product: getAnalyticsProductInfo(deletingProduct.skinProduct),
                    quantity: deletingProduct.quantity,
                    price
                  }],
                  { name: listName, id: listId },
                )
              })

              dispatch({
                action: AppStateActions.ChangeQuantity,
                uid: deletingProduct.uid,
                newQuantity: 0,
              })
            }}
            configuredProduct={deletingProduct}
          />
        )
      }
    </>
  )
}